/**
 * The pipeline route's SE-5508 telemetry, as one call.
 *
 * `MainContainer` is already at its `max-lines-per-function` ratchet, and
 * instrumentation is exactly the kind of concern that accumulates call sites in
 * a screen container over time. Composing the route's two ambient emitters here
 * keeps that growth in a module that is about telemetry, and gives the pair one
 * documented ordering constraint instead of two loose ones.
 *
 * Both are ambient — they observe the route rather than responding to a user
 * action — and both are plain hooks, so they MUST be called above
 * `MainContainer`'s loading/error early returns. Calling them after those
 * returns would make them conditional and break the rules of hooks.
 * @module features/player-kanban/hooks/usePipelineRouteTelemetry
 */

import { usePipelineLoadTime } from "./usePipelineLoadTime";
import { usePipelineTierBlock } from "./usePipelineTierBlock";

/**
 * Report the pipeline route's load time and any tier denial.
 * @param boardId - The active board, or null/undefined while it resolves.
 * @param cardCount - How many cards the board currently holds.
 * @param isPipelineSurface - Whether the resolved surface is a PIPELINE board.
 */
export function usePipelineRouteTelemetry(
  boardId: string | null | undefined,
  cardCount: number,
  isPipelineSurface: boolean
): void {
  // `pipeline_load_time_ms` — SE-5492's first-card-visible measurement, scoped
  // to a PIPELINE surface so a legacy watchlist/roster board never contributes.
  usePipelineLoadTime({ boardId, cardCount, isPipelineSurface });

  // `tier_block_render` — a below-Manager user reaching /player-kanban by
  // direct URL is silently served the legacy board instead of the pipeline
  // (`resolveKanbanSourceType` collapses to a fallback source). Deliberately
  // NOT gated on `isPipelineSurface`: the denial is precisely the case where
  // that flag is false. Manager+ never emits — see the hook.
  usePipelineTierBlock("route");
}
